export type Ho4Article = {
  title: string;
  href?: string;
  description: string;
  keywords: string[];
  status: 'published' | 'coming_soon';
};

export type Ho4ArticleCategory = {
  name: string;
  articles: Ho4Article[];
};

export const HO4_LAST_MODIFIED = 'June 2, 2026';

export const HO4_ARTICLE_CATEGORIES: Ho4ArticleCategory[] = [
  {
    name: 'Fundamentals',
    articles: [
      {
        title: 'What is HO-4 / Renter\'s Insurance?',
        href: '/knowledge-base/what-is-ho4-renters-insurance',
        description: 'Learn what HO-4 renters insurance is, what it covers, and why renters in apartments often need it.',
        keywords: ['ho4', 'renter insurance', 'renters insurance basics', 'coverage overview'],
        status: 'published',
      },
      {
        title: 'How is HO-4 different from other homeowners insurance?',
        href: '/knowledge-base/how-is-ho4-renters-insurance-different-from-homeowners-insurance',
        description: 'Understand the difference between renters insurance and homeowners policies, including building and personal property coverage.',
        keywords: ['ho4 vs homeowners', 'policy differences', 'renters vs homeowners'],
        status: 'published',
      },
      {
        title: 'Is HO-4 worth it?',
        href: '/knowledge-base/is-ho4-worth-it',
        description: 'Review the value of HO-4 based on cost, liability protection, and common renter risks.',
        keywords: ['is renters insurance worth it', 'value', 'benefits'],
        status: 'published',
      },
      {
        title: 'What is loss of use (additional living expenses)?',
        href: '/knowledge-base/what-is-loss-of-use-renters-insurance',
        description: 'Learn what loss of use and ALE coverage pays for when a covered loss makes your rental temporarily uninhabitable.',
        keywords: ['loss of use', 'additional living expenses', 'ALE', 'displacement coverage'],
        status: 'published',
      },
    ],
  },
  {
    name: 'Coverage Decisions',
    articles: [
      {
        title: 'How much liability do I need for HO-4?',
        href: '/knowledge-base/how-much-liability-do-i-need-for-renters-insurance',
        description: 'Get practical guidance for choosing a personal liability limit based on your risk and finances.',
        keywords: ['liability limit', 'personal liability', 'coverage amount'],
        status: 'published',
      },
      {
        title: 'What deductible should I choose for HO-4?',
        href: '/knowledge-base/what-deductible-should-i-choose-for-ho4',
        description: 'Compare deductible options and learn how deductible size affects your monthly premium and claim cost.',
        keywords: ['deductible', 'premium', 'out of pocket'],
        status: 'published',
      },
      {
        title: 'Does HO-4 cover roommates?',
        href: '/knowledge-base/does-ho4-cover-roommates',
        description: 'See how renters insurance works with roommates and whether one policy can protect everyone.',
        keywords: ['roommates', 'shared apartment', 'multiple renters'],
        status: 'published',
      },
      {
        title: 'How much personal property coverage do I need?',
        href: '/knowledge-base/how-much-personal-property-coverage-do-i-need-for-renters-insurance',
        description: 'Estimate how much personal property coverage you need with room-by-room guidance and common limit ranges.',
        keywords: ['personal property coverage', 'how much renters insurance', 'coverage limits', 'belongings'],
        status: 'published',
      },
      {
        title: 'Actual cash value vs. replacement cost',
        href: '/knowledge-base/actual-cash-value-vs-replacement-cost-renters-insurance',
        description: 'Compare ACV and replacement cost settlement on renters insurance and how each affects claim payouts.',
        keywords: ['actual cash value', 'replacement cost', 'ACV vs RCV', 'claim payout'],
        status: 'published',
      },
      {
        title: 'Does renters insurance cover dog bites and pet liability?',
        href: '/knowledge-base/does-renters-insurance-cover-dog-bites-and-pet-liability',
        description: 'Learn when HO-4 personal liability covers dog bite claims, breed restrictions, and how much coverage pet owners need.',
        keywords: ['dog bite renters insurance', 'pet liability', 'breed restrictions', 'renter dog coverage'],
        status: 'published',
      },
    ],
  },
  {
    name: 'Buying & Requirements',
    articles: [
      {
        title: 'How to get HO-4',
        href: '/knowledge-base/how-to-get-ho4-renters-insurance',
        description: 'Follow step-by-step instructions for comparing quotes and buying the right HO-4 policy.',
        keywords: ['how to buy renters insurance', 'quotes', 'get policy'],
        status: 'published',
      },
      {
        title: 'Is HO-4 required by my landlord?',
        href: '/knowledge-base/is-ho4-required-by-landlord',
        description: 'Learn when landlords can require renters insurance and what lease insurance clauses usually mean.',
        keywords: ['landlord requirement', 'lease', 'required renters insurance'],
        status: 'published',
      },
      {
        title: 'How to switch HO-4 insurance companies',
        href: '/knowledge-base/how-to-switch-ho4-insurance-companies',
        description: 'Switch carriers without gaps in coverage by timing cancellation and new policy start dates correctly.',
        keywords: ['switch insurance', 'change carrier', 'cancel policy'],
        status: 'published',
      },
      {
        title: 'How to get proof of renters insurance for your landlord',
        href: '/knowledge-base/how-to-get-proof-of-renters-insurance-for-landlord',
        description: 'Get a declarations page or certificate of insurance and add your landlord as interested party before move-in.',
        keywords: ['proof of insurance', 'certificate of insurance', 'declarations page', 'landlord proof'],
        status: 'published',
      },
    ],
  },
  {
    name: 'Claims, Cost & Situations',
    articles: [
      {
        title: 'How much does HO-4 cost in Texas?',
        href: '/knowledge-base/how-much-does-ho4-cost-in-texas',
        description: 'Estimate typical renters insurance pricing in Texas and what factors influence your premium.',
        keywords: ['cost in texas', 'renters insurance price', 'premium factors'],
        status: 'published',
      },
      {
        title: 'Does HO-4 cover theft (including outside the apartment)?',
        href: '/knowledge-base/does-ho4-cover-theft',
        description: 'Find out when theft is covered, including stolen items outside your home in certain situations.',
        keywords: ['theft coverage', 'stolen belongings', 'off premises coverage'],
        status: 'published',
      },
      {
        title: 'Does HO-4 cover water damage and leaks?',
        href: '/knowledge-base/does-ho4-cover-water-damage',
        description: 'Understand what water damage events are usually covered and what exclusions renters should watch for.',
        keywords: ['water damage', 'leaks', 'pipe burst', 'flood exclusion'],
        status: 'published',
      },
      {
        title: 'Does renters insurance cover fire and smoke damage?',
        href: '/knowledge-base/does-renters-insurance-cover-fire-and-smoke-damage',
        description: 'See when HO-4 covers fire and smoke damage to belongings, liability after a kitchen fire, and temporary housing costs.',
        keywords: ['fire damage renters insurance', 'smoke damage', 'kitchen fire', 'HO-4 fire coverage'],
        status: 'published',
      },
      {
        title: 'Does renters insurance cover flood damage?',
        href: '/knowledge-base/does-renters-insurance-cover-flood-damage',
        description: 'Standard HO-4 excludes flood damage — learn what counts as a flood in Texas and how renters can protect belongings.',
        keywords: ['flood damage renters insurance', 'Texas flood', 'HO-4 flood exclusion', 'NFIP renters'],
        status: 'published',
      },
      {
        title: 'How to file a renters insurance claim',
        href: '/knowledge-base/how-to-file-a-renters-insurance-claim',
        description: 'Use a clear claims checklist so you can document losses, contact your insurer, and avoid delays.',
        keywords: ['file a claim', 'claims process', 'documentation'],
        status: 'published',
      },
      {
        title: 'HO-4 for college students in Texas',
        href: '/knowledge-base/ho4-for-college-students-in-texas',
        description: 'Coverage tips for students living in dorms or apartments, including parent policy considerations.',
        keywords: ['college students', 'dorm coverage', 'student renters insurance'],
        status: 'published',
      },
      {
        title: 'Does renters insurance cover items stolen from my car?',
        href: '/knowledge-base/does-renters-insurance-cover-items-stolen-from-car',
        description: 'Find out when HO-4 covers personal belongings stolen from your vehicle and how off-premises limits apply.',
        keywords: ['stolen from car', 'car break in', 'off premises theft', 'vehicle theft'],
        status: 'published',
      },
      {
        title: 'Does renters insurance cover damage to landlord\'s property?',
        href: '/knowledge-base/does-renters-insurance-cover-damage-to-landlords-property',
        description: 'Learn when liability coverage pays for accidental damage you cause to your rental unit or a neighbor\'s property.',
        keywords: ['landlord property damage', 'liability', 'accidental damage', 'tenant caused damage'],
        status: 'published',
      },
      {
        title: 'Does renters insurance cover mold, bed bugs, or pest damage?',
        href: '/knowledge-base/does-renters-insurance-cover-mold-bed-bugs-pest-damage',
        description: 'Understand what HO-4 covers for mold, bed bugs, and pest infestations — and what is usually excluded.',
        keywords: ['mold coverage', 'bed bugs', 'pest damage', 'infestation exclusion'],
        status: 'published',
      },
      {
        title: 'What happens if you don\'t have renters insurance in Texas?',
        href: '/knowledge-base/what-happens-if-you-dont-have-renters-insurance-in-texas',
        description: 'See the financial and lease risks of going without renters insurance in Texas, including liability exposure.',
        keywords: ['no renters insurance', 'Texas renters', 'uninsured renter', 'lease violation'],
        status: 'published',
      },
    ],
  },
];

